import type { FC, ReactNode } from 'react'
import {
  DragDropContext,
  Draggable,
  type DraggableProvided,
  type DraggableRubric,
  type DraggableStateSnapshot,
  Droppable,
  type DropResult,
} from 'react-beautiful-dnd'
import { createPortal } from 'react-dom'
import { Box, IconButton, Stack } from '@mui/material'
import { PuiTextArea, type QuestionAnswer } from '@pbt/pbt-ui-components'
import { Delete as DeleteIcon } from '@pbt/pbt-ui-components/src/icons'

interface DraggableAnswerListProps {
  answers: QuestionAnswer[]
  disabled?: boolean
  inputIcon?: ReactNode
  onDelete?: (answer: QuestionAnswer) => void
  onEdit: (answer: QuestionAnswer, index: number) => void
  onOrderChange: (oldIndex: number, newIndex: number) => void
  placeholder: string
}

const DraggableAnswerList: FC<DraggableAnswerListProps> = ({
  answers,
  disabled = false,
  inputIcon,
  onDelete,
  onEdit,
  onOrderChange,
  placeholder,
}) => {
  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (!destination || source.index === destination.index) return
    onOrderChange(source.index, destination.index)
  }

  // Renders dragged item via portal to document.body to fix scroll position issues in dialogs/popups
  const renderDraggableClone = (
    provided: DraggableProvided,
    _snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric,
  ) => {
    const answer = answers[rubric.source.index]
    return createPortal(
      <Stack
        direction="row"
        ref={provided.innerRef}
        spacing={1}
        sx={{
          alignItems: 'center',
          backgroundColor: 'action.hover',
          borderRadius: 1,
          boxShadow: 2,
          width: 'auto',
        }}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
      >
        <Box sx={{ display: 'flex', cursor: 'grabbing' }}>{inputIcon}</Box>
        <PuiTextArea
          disabled
          minRows={1}
          placeholder={placeholder}
          sx={{ flex: 1, minWidth: 300 }}
          value={answer?.text || ''}
        />
        {!disabled && onDelete && (
          <IconButton size="small" sx={{ p: 0.5 }}>
            <DeleteIcon />
          </IconButton>
        )}
      </Stack>,
      document.body,
    )
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable
        droppableId="draggable-answer-list"
        renderClone={renderDraggableClone}
      >
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps}>
            {answers.map((answer, index) => (
              <Draggable
                draggableId={answer.id || `answer-${index}`}
                index={index}
                isDragDisabled={disabled}
                key={answer.id || `answer-${index}`}
              >
                {(innerProvided, snapshot) => (
                  <Stack
                    direction="row"
                    ref={innerProvided.innerRef}
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                      mb: 1,
                      backgroundColor: snapshot.isDragging
                        ? 'action.hover'
                        : 'transparent',
                      borderRadius: 1,
                      boxShadow: snapshot.isDragging ? 2 : 0,
                    }}
                    {...innerProvided.draggableProps}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        cursor: disabled ? 'default' : 'grab',
                      }}
                      {...innerProvided.dragHandleProps}
                    >
                      {inputIcon}
                    </Box>
                    <PuiTextArea
                      minRows={1}
                      placeholder={placeholder}
                      sx={{ flex: 1 }}
                      value={answer.text}
                      onChange={({ target: { value } }) =>
                        onEdit({ ...answer, text: value }, index)
                      }
                    />
                    {!disabled && onDelete && (
                      <IconButton
                        size="small"
                        sx={{ p: 0.5 }}
                        onClick={() => onDelete(answer)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Stack>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}

export default DraggableAnswerList
